import { useIsPeopleUnlockButtonLoading } from '@aibees/elephant/components/people-unlock/hooks/use-is-people-unlock-button-loading';
import { doesAiAnalysisHasAnyUpdates } from '@aibees/elephant/components/person-ai-analysis-info/utils';
import { useUnlockPeoplePhoneNumber } from '@aibees/elephant/hooks/people';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { ANALYSIS_STATUS } from '@aibees/elephant/types/personality-analysis';
import { keyframes } from '@mui/material';
import { PeopleEmailStatus, PeopleTableInfo } from '../../types';
import {
  contactDetailsUnlockableItems,
  doesContactDetailHaveAnyUpdates,
  isJobChangeHappened,
} from '../people-drawer/components/contact-details/utils';
import { useHandleUnlockEmails } from '../people-email-popover/hooks/use-handle-unlock-emails';
import {
  getEmailQucikInfoStatus,
  getEmailsCleared,
  isAnyEmailNeedsReverification,
  isEmailBelongsToPreviousCompanies,
} from '../people-email-popover/utils';
import { badgeMap, QuickInfoBadgeInfo } from './types';

const rotateAnimation = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(360deg);
}`;

export function doesEmailEnrichmentHaveAnyUpdates(data?: PeopleTableInfo) {
  const hasExpiredEmail = hasAnyExpiredEmail(data);
  return (
    (!!data?.changes?.EMAIL?.length || hasExpiredEmail) &&
    data?.revelation?.email?.state !== ENRICHMENT_STATE.PROCESSING
  );
}

export function hasAnyExpiredEmail(data?: PeopleTableInfo) {
  const clearedEmails = getEmailsCleared(data?.revelation?.email);

  let hasExpiredEmail = false;

  for (const email of clearedEmails) {
    if (email.status === PeopleEmailStatus.EXPIRED) hasExpiredEmail = true;
  }

  return hasExpiredEmail;
}

// TODO @mehdi-aibees please check all the eslint-disable-next-line react-hooks/rules-of-hooks errors and fix them

export const emailEnrichmentBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(data);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading } = useHandleUnlockEmails(data);
  const needToCheckForUpdates = isEmailBelongsToPreviousCompanies(data);

  if (loading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (hasUpdate || needToCheckForUpdates) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  const needsReverify = isAnyEmailNeedsReverification(data.revelation?.email);
  if (needsReverify) {
    return {
      text: 'B+',
      color: 'success',
    };
  }

  return badgeMap[
    getEmailQucikInfoStatus(getEmailsCleared(data.revelation?.email))
  ];
};

export const phoneNumberEnrichmentBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading, unlocked } = useUnlockPeoplePhoneNumber(data);

  if (loading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (!unlocked) {
    return {
      icon: 'questionMark',
      color: 'default',
    };
  }

  if (unlocked) {
    return {
      text: 'A+',
      color: 'success',
      variant: 'filled',
    };
  }

  // Default fallback
  return {
    icon: 'questionMark',
    color: 'default',
  };
};

export const contactDetailsBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const state = data.revelation?.contact?.state ?? 'default';
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loadingContactDetails } = useIsPeopleUnlockButtonLoading(data);
  const hasUpdate = doesContactDetailHaveAnyUpdates(data);

  if (loadingContactDetails)
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };

  if (hasUpdate) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  switch (state) {
    case 'default':
      return {
        icon: 'questionMark',
        color: 'default',
      };
    case ENRICHMENT_STATE.PROCESSING:
      return {
        icon: 'baseline-autorenew',
        color: 'primary',
        iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
        variant: 'filled',
      };
    case ENRICHMENT_STATE.FAILED:
      return {
        icon: 'info-circle-outline',
        color: 'error',
      };
    case ENRICHMENT_STATE.SUCCESS:
      return {
        text: 'A+',
        color: 'success',
        variant: 'filled',
      };
    default:
      return {
        icon: 'questionMark',
        color: 'default',
      };
  }
};

export const aiAnalysisBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const hasUpdate = doesAiAnalysisHasAnyUpdates(data);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { personalityLoading } = useIsPeopleUnlockButtonLoading(data);
  if (personalityLoading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (hasUpdate) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  return badgeMap[
    data.revelation?.analysis?.state === ENRICHMENT_STATE.FAILED
      ? ANALYSIS_STATUS.ANALYSIS_FAILED
      : data.revelation?.analysis?.content?.status ?? 'default'
  ];
};

export const emailEnrichmentHideBadge: (data: PeopleTableInfo) => boolean = (
  data
) => {
  const isFailed = data.revelation?.email?.state === ENRICHMENT_STATE.FAILED;
  const clearedEmails = getEmailsCleared(data?.revelation?.email);
  const statuses = clearedEmails?.map((i) => i.status);
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(data);
  const needToCheckForUpdates = isEmailBelongsToPreviousCompanies(data);

  if (hasUpdate || needToCheckForUpdates) return false;

  return (
    (isFailed && !statuses?.includes(PeopleEmailStatus.INVALID)) ||
    (!!data?.revelation?.email &&
      data.revelation?.email.state !== ENRICHMENT_STATE.PROCESSING &&
      !clearedEmails.length)
  );
};

export const phoneNumberEnrichmentHideBadge: (
  data: PeopleTableInfo
) => boolean = (data) => {
  const isFailed =
    data.revelation?.phone_cached?.state === ENRICHMENT_STATE.FAILED;
  return isFailed;
};
export const contactDetailsHideBadge: (data: PeopleTableInfo) => boolean = (
  data
) => {
  const unlockableItems = contactDetailsUnlockableItems(data);
  const hasUpdate = doesContactDetailHaveAnyUpdates(data);
  const hasJobChange = isJobChangeHappened(data);

  return !unlockableItems.length && !hasUpdate && !hasJobChange;
};
